import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable1() {
  const pdfRef = useRef();

  const data = [
    {
      2018: 870,
      2019: 857,
      2020: 910,
      2021: 958,
      2022: 1104,
      2023: 1238,
      2024: 1324,
      sex_label: "Female",
    },
    {
      2018: 7010,
      2019: 6613,
      2020: 6499,
      2021: 6951,
      2022: 7519,
      2023: 7799,
      2024: 8102,
      sex_label: "Male",
    },
    {
      2018: 7880,
      2019: 7470,
      2020: 7409,
      2021: 7909,
      2022: 8623,
      2023: 9037,
      2024: 9426,
      sex_label: "Total",
    },
  ];

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];

  // Chart Data
  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Female",
        data: years.map((y) => data[0][y]),
        backgroundColor: "rgba(236, 72, 153, 0.85)",
        borderRadius: 6,
      },
      {
        label: "Male",
        data: years.map((y) => data[1][y]),
        backgroundColor: "rgba(14, 165, 233, 0.85)",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "⛏️ Utah Mining Employment by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: { title: { display: true, text: "Year" }, grid: { display: false } },
      y: { beginAtZero: true, title: { display: true, text: "Employees" } },
    },
  };

  // Insights
  const femaleTotal = data[0]["2024"];
  const maleTotal = data[1]["2024"];
  const growthFemale = (
    ((data[0]["2024"] - data[0]["2018"]) / data[0]["2018"]) *
    100
  ).toFixed(1);
  const growthMale = (
    ((data[1]["2024"] - data[1]["2018"]) / data[1]["2018"]) *
    100
  ).toFixed(1);
  const femaleShare2024 = (
    (femaleTotal / (femaleTotal + maleTotal)) *
    100
  ).toFixed(1);

  // PDF Export
  const downloadPDF = async () => {
    const input = pdfRef.current;
    const canvas = await html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
    pdf.setFontSize(26);
    pdf.text("UTahData.org", 95, 22);
    pdf.line(10, 35, 200, 35);
    pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

    const footerY = 275;
    pdf.line(10, footerY - 5, 200, footerY - 5);
    pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
    pdf.setFontSize(10);
    pdf.text("Built by", 55, footerY + 10);
    pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
    pdf.text("using", 112, footerY + 10);
    pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
    pdf.text("20250916.v3.198", 148, footerY + 10);
    pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 10, 295);
    pdf.text(`From: ${window.location.href}`, 100, 295);

    pdf.save("utah_mining_gender_employment.pdf");
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-yellow-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Utah Mining Employment by Gender (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-pink-800">+{growthFemale}%</p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Male Growth (2018–2024)
            </p>
            <p className="text-xl font-bold text-blue-800">+{growthMale}%</p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Share (2024)
            </p>
            <p className="text-xl font-bold text-green-800">
              {femaleShare2024}%
            </p>
          </div>
        </div>

        {/* Chart + Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Year</th>
                  <th className="p-2">Female</th>
                  <th className="p-2">Male</th>
                  <th className="p-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {years.map((year) => (
                  <tr key={year} className="hover:bg-gray-100">
                    <td className="p-2">{year}</td>
                    <td className="p-2 text-pink-700">
                      {data[0][year].toLocaleString()}
                    </td>
                    <td className="p-2 text-blue-700">
                      {data[1][year].toLocaleString()}
                    </td>
                    <td className="p-2 font-semibold text-gray-900">
                      {data[2][year].toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4 h-[400px]">
            <Bar data={chartData} options={options} />
          </div>
        </div>

        {/* Summary Text */}
        <p className="text-center text-gray-700 mt-4">
          👷 Utah’s mining workforce has steadily grown since 2020, with female
          participation increasing to {femaleShare2024}% by 2024.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
